import { RolesEnum } from './enums/roles.enum';

interface ColorScheme {
  hoverColor: string;
  iconsColor: string;
  mainColor: string;
  textColor: string;
  toggleColor: string;
  messageColor: string;
  peopleColor: string;
  transparentColor: string;
  transparentHoverColor: string;
}

const colorSchemes: Record<string, ColorScheme> = {
  blue: {
    hoverColor: '#055AA9',
    iconsColor: '#177DDC',
    mainColor: '#177DDC',
    textColor: '#fff',
    toggleColor: '#338effa3',
    messageColor: '#D1E1FF',
    peopleColor: '#EC1515',
    transparentColor: '#177DDC33',
    transparentHoverColor: '#177DDC80',
  },
  red: {
    hoverColor: '#9D3939',
    iconsColor: '#E61A27',
    mainColor: '#EF4444',
    textColor: '#fff',
    toggleColor: '#f4000975',
    messageColor: '#FCE3E2',
    peopleColor: '#000',
    transparentColor: '#EF444433',
    transparentHoverColor: '#EF444480',
  },
  green: {
    hoverColor: '#046F4E',
    iconsColor: '#1DA8A0',
    mainColor: '#009163',
    textColor: '#fff',
    toggleColor: '#00bb8e9c',
    messageColor: '#BFE3D8',
    peopleColor: '#EC1515',
    transparentColor: '#00916333',
    transparentHoverColor: '#00916380',
  },
  purple: {
    hoverColor: '#5B46AB',
    iconsColor: '#8D72F4',
    mainColor: '#8D72F4',
    textColor: '#fff',
    toggleColor: '#6600998c',
    messageColor: '#8d73f4a8',
    peopleColor: '#EC1515',
    transparentColor: '#8D72F433',
    transparentHoverColor: '#8D72F480',
  },
  yellow: {
    hoverColor: '#E2A60D',
    iconsColor: '#E59A30',
    mainColor: '#E59A30',
    textColor: '#fff',
    toggleColor: '#ff66008f',
    messageColor: '#e69a31bf',
    peopleColor: '#EC1515',
    transparentColor: '#FFC53033',
    transparentHoverColor: '#FFC53080',
  },
};

const setCssVariable = (name: string, value: string): void => {
  document.documentElement.style.setProperty(name, value);
};

export function setColorScheme(companyColorScheme: string): void {
  const scheme = colorSchemes[companyColorScheme] || colorSchemes.blue;

  setCssVariable('--hover-color', scheme.hoverColor);
  setCssVariable('--icons-color', scheme.iconsColor);
  setCssVariable('--main-color', scheme.mainColor);
  setCssVariable('--text-color', scheme.textColor);
  setCssVariable('--toggle-color', scheme.toggleColor);
  setCssVariable('--message-color', scheme.messageColor);
  setCssVariable('--people-color', scheme.peopleColor);
  setCssVariable('--transparent-color', scheme.transparentColor);
  setCssVariable('--transparent-hover-color', scheme.transparentHoverColor);
}

const darkTheme = {
  layoutBackground: '#191919',
  tableColor: '#303030',
  tableHover: '#3C3C3C',
  tableWrapper: '#202020',
  borderComponent: '#3C3C3C',
  tableCellColor: '#8C8C8C',
  profileBackground: 'transparent',
  activityWrap: '#191919',
  itemBackground: '#363636',
  myMsgColor: '#525252',
  dialogContainerColor: '#303030',
  generalTextColor: '#fff',
  borderColor: '#474747',
};

const darkThemeAdmin = {
  layoutBackground: '#041338',
  tableColor: '#182648',
  tableHover: '#2A3656',
  tableWrapper: '#0E1C40',
  borderComponent: '#2A3656',
  tableCellColor: '#8E95A5',
  dialogContainerColor: '#182648',
  profileBackground: '#0E1C40',
  activityWrap: '#041338',
  itemBackground: '#182648',
  myMsgColor: '#24417A',
  generalTextColor: '#fff',
};

const lightTheme = {
  layoutBackground: '#F8F8F8',
  tableColor: '#FBFBFB',
  tableHover: '#F2F2F2',
  paginatorBackground: '#EBEFF8',
  dialogContainerColor: '#fff',
  generalTextColor: '#0B1F46',
  borderColor: '#E7E9EC',
};

const lightThemeAdmin = {
  layoutBackground: '#EBEFF8',
  tableColor: '#F8FAFF',
  tableHover: '#E7ECF8',
  paginatorBackground: '#EBEFF8',
  generalTextColor: '#0B1F46',
};

export function setModeTheme(role, mode: boolean) {
  if (sessionStorage.getItem('color_scheme') == 'kpmg') {
    setColorScheme('blue');
  }
  if (mode) {
    document.documentElement.style.setProperty(
      '--layout-background',
      '#191919',
    );
    document.documentElement.style.setProperty('--table-color', '#303030');
    document.documentElement.style.setProperty(
      '--section-background',
      '#303030',
    );
    document.documentElement.style.setProperty('--table-hover', '#3C3C3C');
    document.documentElement.style.setProperty('--table-wrapper', '#202020');
    document.documentElement.style.setProperty('--border-component', '#3C3C3C');
    document.documentElement.style.setProperty('--table-cell-color', '#8C8C8C');
    document.documentElement.style.setProperty(
      '--profile-background',
      'transparent',
    );
    document.documentElement.style.setProperty('--activity-wrap', '#191919');
    document.documentElement.style.setProperty('--item-background', '#202020');
    document.documentElement.style.setProperty('--my-msg-color', '#525252');
    document.documentElement.style.setProperty(
      '--mat-dialog-container-color',
      '#303030',
    );
    document.documentElement.style.setProperty('--general-text-color', '#fff');
    document.documentElement.style.setProperty('--border-color', '#474747');
    if (
      role == RolesEnum.admin ||
      sessionStorage.getItem('color_scheme') == 'kpmg'
    ) {
      document.documentElement.style.setProperty(
        '--layout-background',
        '#041338',
      );
      document.documentElement.style.setProperty('--table-color', '#182648');
      document.documentElement.style.setProperty(
        '--section-background',
        '#182648',
      );
      document.documentElement.style.setProperty('--table-hover', '#2A3656');
      document.documentElement.style.setProperty('--table-wrapper', '#0E1C40');
      document.documentElement.style.setProperty(
        '--border-component',
        '#2A3656',
      );
      document.documentElement.style.setProperty(
        '--table-cell-color',
        '#8E95A5',
      );
      document.documentElement.style.setProperty(
        '--mat-dialog-container-color',
        '#182648',
      );
      document.documentElement.style.setProperty(
        '--profile-background',
        '#0E1C40',
      );
      document.documentElement.style.setProperty('--activity-wrap', '#041338');
      document.documentElement.style.setProperty(
        '--item-background',
        '#182648',
      );
      document.documentElement.style.setProperty('--my-msg-color', '#24417A');
    }
  } else {
    document.documentElement.style.setProperty(
      '--layout-background',
      '#F8F8F8',
    );
    document.documentElement.style.setProperty('--table-color', '#FBFBFB');
    document.documentElement.style.setProperty('--section-background', '#FFF');
    document.documentElement.style.setProperty('--table-hover', '#F2F2F2');
    document.documentElement.style.setProperty(
      '--mat-paginator-container-background-color',
      '#EBEFF8',
    );
    document.documentElement.style.setProperty(
      '--mat-dialog-container-color',
      '#fff',
    );
    document.documentElement.style.setProperty(
      '--general-text-color',
      '#0B1F46',
    );
    document.documentElement.style.setProperty('--border-color', '#E7E9EC');
    if (
      role == RolesEnum.admin ||
      sessionStorage.getItem('color_scheme') == 'kpmg'
    ) {
      setColorScheme('kpmg');
      document.documentElement.style.setProperty(
        '--layout-background',
        '#EBEFF8',
      );
      document.documentElement.style.setProperty('--table-color', '#F8FAFF');
      document.documentElement.style.setProperty(
        '--section-background',
        '#FFF',
      );
      document.documentElement.style.setProperty('--table-hover', '#E7ECF8');
      document.documentElement.style.setProperty(
        '--mat-paginator-container-background-color',
        '#EBEFF8',
      );
    }
  }
}
